<template>
    <div class="filter-min-max">
        <div class="filter-label" @click="showOptions = !showOptions">
            <div>{{ t('segments-filters-form') }}: {{ selectedFormName || t('segments-filters-select_form') }}</div>
            <span v-if="conditionsLabel" class="period-label">{{ conditionsLabel }}</span>
            <span v-if="periodLabel" class="period-label">
                {{ t('segments-filters-period_label', { period: periodLabel }) }}
            </span>
        </div>
        <div
            class="filter-options form-responses-options"
            v-if="showOptions"
            v-click-outside="clickedOutsideOptions"
        >
            <div class="fields">
                <div class="field column">
                    <label class="label">{{ t('segments-filters-which_form') }}</label>
                    <select class="form-control" v-model="formId" @change="onFormChange">
                        <option value="">{{ t('segments-filters-select_form') }}</option>
                        <option
                            v-for="form in forms"
                            :key="form.id"
                            :value="form.id"
                        >
                            {{ form.name }}
                        </option>
                    </select>
                </div>

                <div class="field">
                    <label class="checkbox"
                        ><input
                            type="checkbox"
                            v-model="restrictPeriod"
                        />{{ t('segments-filters-limit_time_range') }}</label
                    >
                </div>

                <template v-if="restrictPeriod">
                    <div class="field">
                        <div class="period-title">{{ t('segments-filters-days_ago') }}</div>
                    </div>
                    <div class="field">
                        <label>{{ t('reportFilters-min') }}:</label>
                        <input type="tel" v-model="period.min" />
                    </div>
                    <div class="field">
                        <label>{{ t('reportFilters-max') }}:</label>
                        <input type="tel" v-model="period.max" />
                    </div>
                </template>

                <template v-if="formId">
                    <div class="field">
                        <div class="period-title">{{ t('segments-filters-question_conditions') }}</div>
                    </div>
                    <div v-if="loadingQuestions" class="conditions-loading">
                        {{ t('loading') }}
                    </div>
                    <div v-else-if="availableQuestions.length === 0" class="alert alert-warning">
                        {{ t('segments-filters-no_questions') }}
                    </div>
                    <div v-else class="conditions-wrapper">
                        <condition-group
                            :group="conditionGroup"
                            :level="0"
                            :available-questions="availableQuestions"
                            :compact="true"
                            @change="handleConditionChange"
                        />
                    </div>
                </template>
            </div>
            <div class="filter-actions">
                <button
                    type="button"
                    class="cancelBtn btn btn-sm btn-default"
                    @click="cancel"
                >
                    {{ t('segments-filters-close') }}
                </button>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.filter-min-max {
    position: relative;
    min-width: 0;
}

.form-responses-options {
    position: static;
    width: 100%;
    max-width: 100%;
    padding: 20px;
    margin-top: 12px;
    z-index: 1;
    box-shadow: none;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-sizing: border-box;

    &::before,
    &::after {
        display: none;
    }

    .fields {
        padding: 0;
        min-width: 0;
    }
}

.field {
    display: flex;
    margin-bottom: 12px;

    &.column {
        flex-direction: column;
        padding: 0;
        margin-bottom: 16px;

        label {
            margin-left: 0;
            padding-left: 0;
            margin-bottom: 6px;
            font-size: 13px;
            font-weight: 600;
            color: #374151;
        }
    }

    label {
        width: auto;
        display: block;
        margin-right: 10px;
    }
}

.period-label {
    display: block;
    font-size: 9px;
}

.filter-actions {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
}

label.checkbox {
    font-size: 12px;
    color: #4b5563;

    input {
        margin-right: 6px;
    }
}

.period-title {
    font-size: 13px;
    font-weight: 600;
    margin: 4px 0 0;
    color: #374151;
}

.conditions-wrapper {
    padding: 0;
    max-height: 520px;
    overflow-y: auto;
    min-width: 0;

    ::v-deep .condition-group {
        padding: 14px;
        min-width: 0;
    }

    ::v-deep .group-header {
        margin-bottom: 12px;
        padding-bottom: 10px;
        gap: 12px;
    }

    ::v-deep .group-body .empty-group {
        padding: 28px 16px;
    }
}

.conditions-loading {
    padding: 10px 0;
    font-size: 12px;
    color: #6c757d;
}

input:not([type="checkbox"]) {
    border: none;
    border-bottom: 1px solid #ddd;
    box-shadow: none;
    font-size: 13px;
    flex-grow: 1;
    width: 100%;
    &:focus {
        outline: none;
        box-shadow: none;
        border: none;
        border-bottom: 1px solid #ddd;
    }
}

select.form-control {
    font-size: 13px;
}
</style>
<script>
import keviApi from "../../keviApi.js";
import ConditionGroup from "../../form/questions-conditions/condition-group.vue";

const emptyConditionGroup = () => ({
    conditional_operator: "AND",
    childrens: [],
    conditions: [],
});

export default {
    name: "filter-form-responses",
    components: {
        ConditionGroup,
    },
    props: {
        currentValue: {
            type: [Object, String],
            default: null,
        },
        options: {
            type: Object,
            default: () => ({}),
        },
        name: {
            type: String,
            default: "form-responses",
        },
    },
    data() {
        return {
            showOptions: false,
            clickedOutsideOptions: () => {},
            value: "",
            forms: [],
            formId: "",
            questions: [],
            loadingQuestions: false,
            restrictPeriod: false,
            period: {
                min: null,
                max: null,
            },
            conditionGroup: emptyConditionGroup(),
        };
    },
    async mounted() {
        await this.loadForms();
        if (this.currentValue && typeof this.currentValue === "object") {
            const value = this.currentValue;
            this.formId = value.formId || "";
            this.restrictPeriod = !!value.restrictPeriod;
            if (value.period) {
                this.period.min = value.period.min;
                this.period.max = value.period.max;
            }
            if (value.conditions && Object.keys(value.conditions).length > 0) {
                this.conditionGroup = JSON.parse(JSON.stringify(value.conditions));
            }
            if (this.formId) {
                this.loadQuestions();
            }
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        loadForms() {
            const options = keviApi.getOptions("forms");
            return this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.forms = response.body || [];
                }
            });
        },
        loadQuestions() {
            if (!this.formId) {
                this.questions = [];
                return;
            }
            this.loadingQuestions = true;
            const options = keviApi.getOptions(`forms/${this.formId}`);
            this.$http(options).then(
                (response) => {
                    if (response.status === 200) {
                        this.questions = response.body.questions || [];
                    }
                    this.loadingQuestions = false;
                },
                () => {
                    this.loadingQuestions = false;
                },
            );
        },
        onFormChange() {
            this.conditionGroup = emptyConditionGroup();
            this.questions = [];
            this.loadQuestions();
            this.setValue();
        },
        handleConditionChange(updatedGroup) {
            this.conditionGroup = updatedGroup;
            this.setValue();
        },
        cancel() {
            this.showOptions = false;
        },
        checkValuesOrder() {
            if (
                this.period.max &&
                this.getFloat(this.period.max) < this.getFloat(this.period.min)
            ) {
                const periodMax = this.period.max;
                const periodMin = this.period.min;
                this.period.max = periodMin;
                this.period.min = periodMax;
            }
        },
        setValue() {
            this.checkValuesOrder();
            this.value = {
                formId: this.formId,
                restrictPeriod: this.restrictPeriod ? 1 : "",
                period: {
                    min: this.getFloat(this.period.min),
                    max: this.getFloat(this.period.max),
                },
                conditions: JSON.parse(JSON.stringify(this.conditionGroup)),
            };
        },
        getFloat(n) {
            if (n === null || n === undefined || n === "") {
                return null;
            }
            return parseFloat(
                String(n)
                    .replace(/[^0-9,]/gi, "")
                    .replace(",", "."),
            );
        },
        changed() {
            this.$emit("changed", this.value);
        },
    },
    computed: {
        selectedFormName() {
            const form = this.forms.find(
                (f) => String(f.id) === String(this.formId),
            );
            return form ? form.name : "";
        },
        availableQuestions() {
            return this.questions.filter((q) => !q.removed);
        },
        conditionsCount() {
            const count = (group) => {
                if (!group) return 0;
                let total = (group.conditions || []).length;
                (group.childrens || []).forEach((child) => {
                    total += count(child);
                });
                return total;
            };
            return count(this.conditionGroup);
        },
        conditionsLabel() {
            if (!this.conditionsCount) {
                return "";
            }
            return this.t("segments-filters-conditions_count", {
                count: this.conditionsCount,
            });
        },
        periodLabel() {
            if (!this.restrictPeriod) {
                return "";
            }
            if (!this.period.min && !this.period.max) {
                return "";
            } else if (!this.period.min && this.period.max) {
                return this.t("segments-filters-until") + " " + this.period.max;
            } else if (!this.period.max && this.period.min) {
                return this.t("segments-filters-more_than") + " " + this.period.min;
            } else {
                return (
                    this.t("segments-filters-between") +
                    " " +
                    this.period.min +
                    " " +
                    this.t("segments-filters-and") +
                    " " +
                    this.period.max
                );
            }
        },
    },
    watch: {
        showOptions() {
            this.setValue();
            if (this.showOptions) {
                window.setTimeout(() => {
                    this.clickedOutsideOptions = () => {
                        if (this.showOptions) {
                            this.cancel();
                        }
                    };
                }, 100);
            } else {
                this.clickedOutsideOptions = () => {};
            }
        },
        restrictPeriod() {
            this.setValue();
        },
        "period.min"() {
            this.setValue();
        },
        "period.max"() {
            this.setValue();
        },
        value() {
            this.changed();
        },
    },
};
</script>
